import { Component, Input, SimpleChanges, AfterViewInit, ElementRef, ViewChildren, QueryList, OnChanges, OnDestroy } from '@angular/core';
import { FilterCard } from 'src/app/core/models/filter-card.model';
import { AppDataColorService } from '../../services/app-data-color.service';
import { AppVisualContextService } from '../../services/app-visual-context.service';

@Component({
  selector: 'app-filter-card-line',
  templateUrl: './filter-card-line.component.html',
  styleUrl: './filter-card-line.component.css',
})
export class FilterCardLineComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input({ required: true }) cards: FilterCard[];
  @ViewChildren('labelElement') labelElements: QueryList<ElementRef>;
  protected isProcessing: boolean = false;
  protected colorClasses: string[] = [];
  private leasedColors = new Map<string, string>();
  private overflowTimer?: ReturnType<typeof setTimeout>;
  private processingTimer?: ReturnType<typeof setTimeout>;

  private readonly PAUSE_DURATION = 0.5;
  private readonly ANIMATION_SPEED = 50;

  constructor(private dataColors: AppDataColorService, private visualContext: AppVisualContextService) {}

  ngAfterViewInit() {
    this.scheduleLabelOverflow();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['cards']) {
      this.updateColorClasses();
      this.scheduleLabelOverflow();
    }
  }

  private scheduleLabelOverflow(): void {
    clearTimeout(this.overflowTimer);
    this.overflowTimer = setTimeout(() => this.checkLabelOverflow(), 100);
  }

  checkLabelOverflow() {
    if (this.labelElements) {
      this.labelElements.forEach(labelRef => {
        const labelDiv = labelRef.nativeElement as HTMLElement;
        const paragraph = labelDiv.querySelector('p') as HTMLElement;

        if (paragraph) {
          const textWidth = paragraph.getBoundingClientRect().width;
          const containerWidth = labelDiv.getBoundingClientRect().width;

          if (textWidth > containerWidth) {
            const overflow = paragraph.scrollWidth - containerWidth;
            const slideDistance = overflow + 5;
            const slideDuration = slideDistance / this.ANIMATION_SPEED;
            const totalDuration = this.PAUSE_DURATION * 2 + slideDuration;

            paragraph.style.setProperty('--slide-distance', `-${slideDistance}px`);
            paragraph.style.animationDuration = `${totalDuration}s`;
            labelDiv.classList.add('animate');
          } else {
            labelDiv.classList.remove('animate');
            paragraph.style.removeProperty('--slide-distance');
            paragraph.style.removeProperty('animation-duration');
          }
        }
      });
    }
  }

  private updateColorClasses(): void {
    // Valida o conjunto inteiro antes de modificar as regras usadas por outra instância.
    const colors = this.cards.map(card => card.dataColor ? this.dataColors.normalize(card.dataColor) : undefined);
    const classes = this.cards.map((card, index) => this.visualContext.classForFilter(card.type) ?? (colors[index] ? undefined : 'app-filter-category-default'));
    const leases = new Set(colors.filter((color, index): color is string => !!color && !classes[index]));
    for (const color of leases) if (!this.leasedColors.has(color)) this.leasedColors.set(color, this.dataColors.acquire(color));
    for (const color of this.leasedColors.keys()) {
      if (!leases.has(color)) {
        this.dataColors.release(color);
        this.leasedColors.delete(color);
      }
    }
    this.colorClasses = classes.map((className, index) => className ?? this.leasedColors.get(colors[index]!)!);
  }

  changeCard(card: FilterCard) {
    if (this.isProcessing) return;
    this.isProcessing = true;
    this.processingTimer = setTimeout(() => (this.isProcessing = false), 1000);

    if (card.selected) {
      card.selected = false;
      this.runCallBack(card.callBacks.unSelect.function, card.callBacks.unSelect.params);
    } else {
      this.cards.forEach(c => (c.selected = false));
      card.selected = true;
      this.runCallBack(card.callBacks.select.function, card.callBacks.select.params);
    }
  }

  runCallBack(callBack: (...args: any[]) => void, params?: { [name: string]: any }) {
    params ? callBack(...Object.values(params)) : callBack();
  }

  trackByIndex(index: number): number {
    return index;
  }

  ngOnDestroy(): void {
    clearTimeout(this.overflowTimer);
    clearTimeout(this.processingTimer);
    for (const color of this.leasedColors.keys()) this.dataColors.release(color);
    this.leasedColors.clear();
  }
}
